/**
 * @file `<AssessmentPhase/>` — the reader's adaptive Part-assessment surface
 *       (umbrella #1357).
 * @module @labcraft/lab/ui/assessment/assessment_phase
 *
 * The lab-side UI for the Part-boundary CAT mastery assessment (umbrella
 * #1357; repurposed from the retired per-chapter quiz #1208). It drives one
 * screen off a {@link QuizControl} the `PartAssessmentPage` owns (a
 * `QuizState` + `PartAssessmentController.view` + `QuizEngine`): while the
 * attempt is unsettled it renders the progress/score chrome + a per-kind
 * question renderer (mc / command / fill-blank); once settled it renders the
 * verdict screen — a celebratory pass, or a fail carrying per-weak-concept
 * remediation links plus a fresh-draw re-attempt.
 *
 * Design vocabulary is deliberately the objective card's (course-kit
 * `objective.module.css`): the same card container, rail accent, uppercase
 * code-font labels, and accent primary button, using the shared semantic
 * tokens so both themes hold.
 *
 * Load-bearing invariants this file honors:
 *
 *   - **Deterministic grading stays in the control.** A renderer never
 *     grades; it hands `control.submit(...)` a typed {@link QuizSubmission}
 *     and the control grades via `QuizEngine.grade` before advancing. No LLM,
 *     no grading logic here (umbrella #1203 Q1).
 *   - **MC options shuffle AND re-id per presentation.** The authored
 *     `options[0]` is the correct answer by convention (authored with the
 *     constant id `'a'`), so the `McRenderer` shuffles the display order
 *     (`Math.random`, display-only) so position never leaks the answer, AND
 *     stamps each rendered option a per-presentation display id (its shuffled
 *     index) rather than its authored id — so the DOM `data-testid` never
 *     exposes `'a'` as a constant "this is correct" tell. The shuffle+remap is
 *     **stable within one presentation** — memoized on the question object
 *     identity, so it re-runs only when a new question is drawn (including a
 *     fresh-draw retry that re-serves the same id), never on a re-render (a
 *     keystroke, a selection). On submit the display id is mapped back to the
 *     authored id, so the engine grades against the unshuffled authored
 *     question — the renderer never grades.
 *   - **The assessment owns no forward navigation.** On a `pass` it fires
 *     `onPass` (the write side of the Part gate — the page persists the pass
 *     via `recordPartAssessment`) and shows a celebratory verdict; the page
 *     owns where the learner goes next. On a `fail` it renders remediation
 *     links (one per weak concept) that route back to the owning chapters via
 *     `onNavigate`, plus a secondary fresh-draw re-attempt.
 *   - **`prompt` is authored, trusted content**; option/prompt text is
 *     rendered as text (course prose is author-trusted, but there is no need
 *     to dangerouslySet here — the prompt is plain text, not MDX).
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react'
import type {
  QuizControl,
  QuizSubmission,
  QuizView,
} from '@labcraft/course-kit/runtime'
import type { NodeId, QuizOption, QuizQuestion } from '@labcraft/course-kit'
import { CheckIcon, InfoCircleIcon } from '@labcraft/ui/icons'
import styles from './assessment_phase.module.css'

/**
 * One remediation link on the fail screen (umbrella #1357). Resolved by the
 * `PartAssessmentPage` from each of `view.weakConcepts` against the Part's
 * `RemediationTarget` (authored) or the owning chapter (derived), then handed
 * down so `AssessmentResult` stays free of the resolution logic.
 *
 * @property concept - the weak course-graph node this link remediates
 *   (`NodeId`); the React key + used in the label
 * @property chapterTitle - the owning chapter's display title (the link text)
 * @property href - the SPA route back to the owning chapter, optionally with a
 *   `#reference-card` fragment (`/courses/:courseId/:partId/:chapterId[#card]`)
 * @property objectiveIds - the owning chapter's objective ids relevant to this
 *   concept (surfaced as a sub-label)
 */
export interface RemediationLink {
  concept: NodeId
  chapterTitle: string
  href: string
  objectiveIds: string[]
}

/**
 * What the assessment phase needs from the `PartAssessmentPage`: the engine
 * control, a pass-persistence callback, the resolved remediation links, and a
 * navigation callback for a remediation click.
 *
 * @property control - the {@link QuizControl} the page owns (view + submit +
 *   retry over a `QuizState`)
 * @property onPass - invoked once when the attempt settles on `pass`, with the
 *   weighted `score` (`view.earned`) banked at pass time. The page persists it
 *   via `recordPartAssessment` — the write side of the Part gate. Fire-once
 *   per settle: the phase de-dupes internally so a re-render on the pass screen
 *   doesn't re-post.
 * @property remediation - the resolved remediation links (empty on a pass);
 *   the fail screen renders one per entry
 * @property onNavigate - route to a remediation link's `href` (reuses the
 *   chapter footer's `onFooterNavigate` contract)
 */
export interface AssessmentPhaseProps {
  control: QuizControl
  onPass: (score: number) => void
  remediation: RemediationLink[]
  onNavigate: (href: string) => void
}

/**
 * Mount the assessment surface off the page-owned {@link QuizControl}.
 * Branches on the view's `status`:
 *
 *   - `pass` / `fail` → the {@link AssessmentResult} verdict screen (a fail carries
 *     the remediation links);
 *   - `in-progress` / `continue` → the {@link AssessmentProgress} chrome + the
 *     per-kind {@link AssessmentQuestionCard}. If the draw is exhausted
 *     (`question === null` while unsettled), a fallback exhaustion message
 *     shows — the bank ran out before a decision, a lint-guarded authoring
 *     error, surfaced rather than white-screened.
 *
 * When the verdict settles on `pass`, `onPass` fires exactly once (guarded by
 * a ref so re-renders on the pass screen don't re-post) so the page persists
 * the pass.
 *
 * @param props - the control + pass-persistence + remediation wiring
 * @returns the assessment-phase element
 */
export function AssessmentPhase(props: AssessmentPhaseProps): JSX.Element {
  const { control, onPass, remediation, onNavigate } = props
  const view = control.view
  const passed = view.status === 'pass'

  // Fire `onPass` once per settle. The ref latches on the first `pass`
  // render so a re-render on the (still-`pass`) screen doesn't re-post; a
  // fresh-draw re-attempt resets the decision to unsettled, which clears the
  // latch so a later pass fires again.
  const passReportedScore = useRef<number | null>(null)
  const earned = view.earned
  useEffect(() => {
    if (!passed) {
      passReportedScore.current = null
      return
    }
    if (passReportedScore.current !== null) return
    passReportedScore.current = earned
    onPass(earned)
  }, [passed, earned, onPass])

  const settled = view.status === 'pass' || view.status === 'fail'

  if (settled) {
    return (
      <div className={styles.phase} data-testid="assessment-phase">
        <AssessmentResult
          view={view}
          onRetry={control.retry}
          remediation={remediation}
          onNavigate={onNavigate}
        />
      </div>
    )
  }

  return (
    <div className={styles.phase} data-testid="assessment-phase">
      <AssessmentProgress view={view} />
      {view.question ? (
        // Key on the question id so a new draw remounts the card, resetting
        // each renderer's local input (selection / typed text) — otherwise the
        // prior question's selection carries into the next (like-id'd options).
        <AssessmentQuestionCard
          key={view.question.id}
          question={view.question}
          onSubmit={control.submit}
        />
      ) : (
        <AssessmentExhausted />
      )}
    </div>
  )
}

/**
 * Human label for a {@link QuizQuestion.kind}, shown on the card head next to
 * the marker. Kept a small record rather than inline so the three kinds read
 * as one closed vocabulary the way the objective card's `STATUS_LABEL` does.
 */
const KIND_LABEL: Record<QuizQuestion['kind'], string> = {
  mc: 'Multiple choice',
  command: 'Predict the command',
  'fill-blank': 'Fill in the blank',
}

/**
 * Progress + score chrome for an in-flight attempt. Two rows over a
 * segmented budget bar (one segment per `maxQuestions` slot):
 *
 *   - the asked / maxQuestions count (the ladder budget axis);
 *   - the earned-toward-threshold score + the current difficulty band.
 *
 * Mirrors `<ChapterProgress/>`'s idiom so the assessment phase reads as the same
 * reader chrome the objectives phase used.
 *
 * @param props.view - the current {@link QuizView}
 * @returns the progress chrome element
 */
function AssessmentProgress(props: { view: QuizView }): JSX.Element {
  const { asked, maxQuestions, earned, threshold, max, band } = props.view
  return (
    <div className={styles.progress} data-testid="assessment-progress">
      <div className={styles.progressMeta}>
        <span className={styles.progressLabel}>Part assessment</span>
        <span className={styles.progressCount} data-testid="assessment-asked">
          <span className={styles.countCurrent}>{asked}</span>
          <span className={styles.countOf}>/ {maxQuestions}</span>
        </span>
      </div>
      <div
        className={styles.bar}
        role="progressbar"
        aria-valuenow={asked}
        aria-valuemin={0}
        aria-valuemax={maxQuestions}>
        {Array.from({ length: maxQuestions }, (_, idx) => {
          const isDone = idx < asked
          const isActive = idx === asked
          const className = [
            styles.seg,
            isDone ? styles.segDone : '',
            isActive ? styles.segActive : '',
          ]
            .filter(Boolean)
            .join(' ')
          return <span key={idx} className={className} />
        })}
      </div>
      <div className={styles.score}>
        <span data-testid="assessment-score">
          <span className={styles.scoreEarned}>{formatPoints(earned)}</span>
          {' / '}
          {formatPoints(threshold)} to pass
          <span className={styles.countOf}> (of {formatPoints(max)})</span>
        </span>
        <span className={styles.band} data-testid="assessment-band">
          Band {band}
        </span>
      </div>
    </div>
  )
}

/**
 * Round a weighted-points value for display — the engine's `earned` can be a
 * fraction after the prior-wrong discount, so a raw float would read noisily.
 * One decimal place, trailing `.0` trimmed, keeps the chrome legible while
 * still showing a discounted partial credit.
 *
 * @param n - the weighted-points value
 * @returns a compact display string (e.g. `12`, `12.6`)
 */
function formatPoints(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

/**
 * The inline spans an assessment bank authors, in precedence order: inline code
 * (backticks — inner text is literal), then bold (`**…**`), then italic
 * (`*…*`). Each alternative captures its own inner text.
 */
const INLINE_SPAN = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*/g

/**
 * Render one line of inline markdown as React nodes. Assessment prompts and
 * options are authored with the same inline code and emphasis the chapters use
 * (`du -sh`, `--closure-size`), but this renderer is course-agnostic and prints
 * strings, so without this the markers show literally (#1650). Only inline
 * spans are handled — no block-level markdown, no links — and every span
 * becomes a real React element, never injected HTML, so the authored (trusted)
 * string can't smuggle markup: the plain-text safety the file header called for
 * is kept. An unmatched marker (a lone backtick) falls through as literal text.
 *
 * @param text - the authored prompt or option string
 * @returns the string parsed into text plus `code` / `strong` / `em` nodes
 */
function renderInlineMarkdown(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let last = 0
  let key = 0
  for (const m of text.matchAll(INLINE_SPAN)) {
    const at = m.index ?? 0
    if (at > last) nodes.push(text.slice(last, at))
    if (m[1] !== undefined)
      nodes.push(
        <code key={key++} className={styles.code}>
          {m[1]}
        </code>,
      )
    else if (m[2] !== undefined) nodes.push(<strong key={key++}>{m[2]}</strong>)
    else nodes.push(<em key={key++}>{m[3]}</em>)
    last = at + m[0].length
  }

  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

/**
 * The per-kind question card. Dispatches on `question.kind` to the matching
 * renderer, wrapping each in the shared card chrome (rail + head + prompt).
 * Each renderer owns its local input state and, on submit, calls `onSubmit`
 * with the typed {@link QuizSubmission} for its kind — the hook grades.
 *
 * @param props.question - the current question to render
 * @param props.onSubmit - `control.submit`, called with the typed
 *   submission
 * @returns the question card element
 */
function AssessmentQuestionCard(props: {
  question: QuizQuestion
  onSubmit: (submission: QuizSubmission) => void
}): JSX.Element {
  const { question, onSubmit } = props
  return (
    <div className={styles.card} data-testid="assessment-question">
      <span className={styles.rail} aria-hidden />
      <div className={styles.cardHead}>
        <span className={styles.marker} aria-hidden>
          <InfoCircleIcon />
        </span>
        <span className={styles.kindLabel}>{KIND_LABEL[question.kind]}</span>
      </div>
      <p className={styles.prompt} data-testid="assessment-prompt">
        {renderInlineMarkdown(question.prompt)}
      </p>
      {question.kind === 'mc' ? (
        <McRenderer question={question} onSubmit={onSubmit} />
      ) : (
        <FreeTextRenderer question={question} onSubmit={onSubmit} />
      )}
    </div>
  )
}

/**
 * Fisher–Yates shuffle of an `mc` question's options into a fresh array, so
 * display order never leaks the authored `options[0]`-is-correct convention.
 * Display-only randomness — `Math.random` is fine here; the caller
 * ({@link McRenderer}) re-ids the shuffled options per presentation and maps
 * the selection back to the authored id before submitting, and the engine
 * grades the **unshuffled** authored question, so a shuffle can never
 * mis-grade. The input is not mutated.
 *
 * @param options - the authored option list (`options[0]` correct)
 * @returns a new array of the same options in random order
 */
function shuffleOptions(options: readonly QuizOption[]): QuizOption[] {
  const out = [...options]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

/**
 * Multiple-choice renderer — a radio-group of selectable option rows. The
 * learner picks one (local state), then submits `{ kind: 'mc', optionId }`.
 * Options are read off the question's `mc` answer variant — a narrowing the
 * caller has already guaranteed by dispatching on `question.kind === 'mc'`,
 * re-asserted here defensively.
 *
 * Two layers hide the correct answer, because the authored `options[0]` is
 * always correct (and authored with the constant id `'a'`):
 *
 *   - **Display order is shuffled per presentation**, so screen position
 *     never tracks correctness.
 *   - **Each rendered option carries a per-presentation display id** (its
 *     shuffled index), NOT its authored id. The `data-testid` and the local
 *     selection use the display id, so the DOM never exposes the authored
 *     `'a'` as a constant "this is correct" tell. On submit the display id is
 *     mapped back to the authored id, which the engine grades against the
 *     unshuffled authored question — the renderer still never grades.
 *
 * The shuffle+remap is memoized on the `question` object identity: it re-runs
 * when a new question is drawn (a fresh reference from the engine, including a
 * fresh-draw retry that re-serves the same id), never on a re-render — so
 * options don't jump on every selection / keystroke.
 *
 * @param props.question - the `mc` question (its answer's `options`)
 * @param props.onSubmit - `control.submit`
 * @returns the multiple-choice renderer element
 */
function McRenderer(props: {
  question: QuizQuestion
  onSubmit: (submission: QuizSubmission) => void
}): JSX.Element {
  const [selected, setSelected] = useState<string | null>(null)
  const answer = props.question.answer
  const authored = answer.kind === 'mc' ? answer.options : []
  // Shuffle once per presentation, then assign each displayed option a
  // per-presentation display id (its shuffled position). The authored
  // `options[0]`-is-correct convention labels the correct option a constant id
  // ('a'); surfacing that id in the DOM (`data-testid`) or carrying it as the
  // selection would leak the answer despite the position shuffle. The submit
  // handler maps the display id back to the authored id, which the engine
  // grades — the renderer still never grades. Keyed on `props.question` (a
  // fresh reference each draw) so a re-render reuses the same order.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(
    () =>
      shuffleOptions(authored).map((option, index) => ({
        displayId: String(index),
        authoredId: option.id,
        text: option.text,
      })),
    [props.question],
  )

  return (
    <>
      <ul className={styles.options} role="radiogroup" aria-label="Options">
        {options.map(option => {
          const isSelected = selected === option.displayId
          const className = [
            styles.option,
            isSelected ? styles.optionSelected : '',
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <li key={option.displayId}>
              <button
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={className}
                data-testid={`assessment-option-${option.displayId}`}
                onClick={() => setSelected(option.displayId)}>
                <span className={styles.optionDot} aria-hidden />
                <span>{renderInlineMarkdown(option.text)}</span>
              </button>
            </li>
          )
        })}
      </ul>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.submit}
          data-testid="assessment-submit"
          disabled={selected === null}
          onClick={() => {
            if (selected === null) return
            const authoredId = options.find(
              o => o.displayId === selected,
            )?.authoredId
            if (authoredId === undefined) return
            props.onSubmit({ kind: 'mc', optionId: authoredId })
          }}>
          Submit answer
        </button>
      </div>
    </>
  )
}

/**
 * Free-text renderer for the `command` and `fill-blank` kinds — a single
 * text input plus a submit. On submit it hands
 * `{ kind, text }` to `control.submit`; the hook normalizes + matches
 * against the authored `accept` set (`QuizEngine.grade`). The command
 * variant styles the input as a code line and labels it "command"; the
 * fill-blank variant reads as prose. Submit is disabled on an empty input so
 * a blank submission (which could never grade true) can't burn a question.
 *
 * The two kinds share one renderer because their submission shape is
 * identical (`{ kind, text }`) — only the label + monospace styling differ,
 * exactly the split the engine's `QuizSubmission` union already draws.
 *
 * @param props.question - the `command` or `fill-blank` question
 * @param props.onSubmit - `control.submit`
 * @returns the free-text renderer element
 */
function FreeTextRenderer(props: {
  question: QuizQuestion
  onSubmit: (submission: QuizSubmission) => void
}): JSX.Element {
  const [text, setText] = useState('')
  const kind = props.question.kind
  // Defensive: the caller dispatches the `mc` kind elsewhere, so `kind` is
  // one of the two free-text kinds here; narrow for the submission type.
  const freeKind: 'command' | 'fill-blank' =
    kind === 'command' ? 'command' : 'fill-blank'
  const isCommand = freeKind === 'command'

  const submit = (): void => {
    if (text.trim() === '') return
    props.onSubmit({ kind: freeKind, text })
  }

  return (
    <>
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="assessment-freetext">
          {isCommand ? 'Command' : 'Your answer'}
        </label>
        <input
          id="assessment-freetext"
          type="text"
          className={[styles.input, isCommand ? styles.inputCommand : '']
            .filter(Boolean)
            .join(' ')}
          data-testid="assessment-input"
          value={text}
          spellCheck={!isCommand}
          autoComplete="off"
          placeholder={isCommand ? 'e.g. ls -la' : 'Type your answer'}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
        />
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.submit}
          data-testid="assessment-submit"
          disabled={text.trim() === ''}
          onClick={submit}>
          Submit answer
        </button>
      </div>
    </>
  )
}

/**
 * The pass / fail verdict screen. Same card container as a question, tinted
 * by outcome: a done-green pass is a celebratory confirmation (the page owns
 * where the learner goes next — the verdict carries no forward button of its
 * own); a coral fail carries the per-weak-concept remediation links plus a
 * secondary fresh-draw re-attempt (`control.retry`). Fail is styled as a
 * first-class "review + draw again" state, never a red error — mirroring the
 * objective card's rethink posture.
 *
 * @param props.view - the settled {@link QuizView} (`status` is pass/fail)
 * @param props.onRetry - start a fresh-draw re-attempt (fail only)
 * @param props.remediation - the resolved remediation links (empty on a pass)
 * @param props.onNavigate - route to a remediation link's `href`
 * @returns the verdict screen element
 */
function AssessmentResult(props: {
  view: QuizView
  onRetry: () => void
  remediation: RemediationLink[]
  onNavigate: (href: string) => void
}): JSX.Element {
  const passed = props.view.status === 'pass'
  const className = [
    styles.result,
    passed ? styles.resultPass : styles.resultFail,
  ].join(' ')

  return (
    <div
      className={className}
      data-testid={passed ? 'assessment-pass' : 'assessment-fail'}
      role="status"
      aria-live="polite">
      <span className={styles.rail} aria-hidden />
      <span className={styles.resultMarker} aria-hidden>
        {passed ? <CheckIcon width={20} height={20} /> : <InfoCircleIcon />}
      </span>
      <span className={styles.resultEyebrow}>
        {passed ? 'Part assessment passed' : 'Not quite yet'}
      </span>
      <h2 className={styles.resultTitle}>
        {passed ? 'You’ve mastered this Part' : 'Give it another draw'}
      </h2>
      <p className={styles.resultBody}>
        {passed
          ? 'You cleared the mastery bar for this Part — you’re ready to move on.'
          : 'That attempt fell short of the mastery bar. Review the concepts below, then draw a fresh set — no cooldown, no penalty.'}
      </p>
      <p className={styles.resultScore} data-testid="assessment-result-score">
        {formatPoints(props.view.earned)} / {formatPoints(props.view.threshold)}{' '}
        points
      </p>
      {!passed && props.remediation.length > 0 && (
        <ul className={styles.remediation} data-testid="assessment-remediation">
          {props.remediation.map(link => (
            <li key={link.concept}>
              <button
                type="button"
                className={styles.remediationLink}
                data-testid={`assessment-remediation-${link.concept}`}
                onClick={() => props.onNavigate(link.href)}>
                Review: {link.chapterTitle}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!passed && (
        <div className={styles.resultActions}>
          <button
            type="button"
            className={styles.retry}
            data-testid="assessment-retry"
            onClick={props.onRetry}>
            Try a fresh draw
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Shown when the draw is exhausted mid-attempt (`question === null` while the
 * status is still unsettled) — the bank ran out of unused items for the
 * concept before the engine reached a decision. Lint (Part D) guards the
 * `minBankSize` that makes this unreachable in a well-authored chapter, so
 * this is a defensive floor: surface an honest message rather than a blank
 * card. No retry here — a re-draw would hit the same exhausted bank.
 *
 * @returns the exhaustion message element
 */
function AssessmentExhausted(): JSX.Element {
  return (
    <div
      className={styles.card}
      data-testid="assessment-exhausted"
      role="status">
      <span className={styles.rail} aria-hidden />
      <div className={styles.cardHead}>
        <span className={styles.marker} aria-hidden>
          <InfoCircleIcon />
        </span>
        <span className={styles.kindLabel}>Assessment</span>
      </div>
      <p className={styles.prompt}>
        This Part’s question bank ran out before a decision. That’s a content
        gap — reload to try again. Your chapter progress is saved.
      </p>
    </div>
  )
}
